import { Link } from "react-router-dom";
import { useState } from "react";
import { newsletterApi } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { t } from "@/lib/i18n";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await newsletterApi.subscribe({ email, market: "NO", consent });
      toast.success(t.footer.welcomeToast);
      setEmail("");
    } catch (_) {
      toast.error(t.footer.genericError);
    }
  };

  return (
    <footer className="mt-24 md:mt-32 border-t border-[color:var(--marcini-border)] bg-white">
      <div className="px-5 md:px-12 py-16 md:py-20 grid gap-14 md:grid-cols-[1.4fr_1fr_1fr]">
        <div>
          <p className="text-eyebrow mb-4">{t.footer.newsletterEyebrow}</p>
          <h3 className="font-serif-editorial text-3xl md:text-4xl leading-tight mb-4 max-w-md">
            {t.footer.newsletterTitle}
          </h3>
          <p className="text-[color:var(--marcini-fg-2)] mb-6 max-w-md text-sm">{t.footer.newsletterBody}</p>
          <form onSubmit={submit} className="flex items-center border-b border-black py-2 max-w-md" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t.footer.newsletterPlaceholder}
              className="flex-1 bg-transparent outline-none text-sm py-2"
              data-testid="newsletter-email"
            />
            <button type="submit" data-testid="newsletter-submit" className="ml-2 p-2" aria-label="Meld på">
              <ArrowRight size={18} />
            </button>
          </form>
          <label className="mt-3 flex items-start gap-2 text-xs text-[color:var(--marcini-fg-2)]">
            <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} data-testid="newsletter-consent" className="mt-0.5" />
            <span>{t.footer.newsletterConsent}</span>
          </label>
        </div>
        <FooterCol title={t.footer.col1} links={[
          ["Alle produkter", "/shop"],
          ["Nye produkter", "/collections/nye-produkter"],
          ["Hoodies", "/collections/marcini-hoodies-unisex"],
          ["Caps", "/collections/marcini-capses-unisex"],
          ["Lookbook", "/lookbook"],
        ]} />
        <FooterCol title={t.footer.col3} links={[
          ["Kontakt oss", "/pages/contact"],
          ["Vilkår og betingelser", "/policies/terms-of-service"],
          ["Returvilkår", "/policies/refund-policy"],
          ["Personvernerklæring", "/policies/privacy-policy"],
          ["Ønskeliste", "/wishlist"],
        ]} />
      </div>
      <div className="px-5 md:px-12 py-6 border-t border-[color:var(--marcini-border)] text-eyebrow text-[color:var(--marcini-fg-3)]">
        <span>{t.footer.tagline.replace("{year}", new Date().getFullYear())}</span>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <p className="text-eyebrow mb-4">{title}</p>
      <ul className="space-y-3 text-sm">
        {links.map(([label, href]) => {
          const external = href.startsWith("http");
          return (
            <li key={label}>
              {external ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline"
                >{label}</a>
              ) : (
                <Link to={href} className="link-underline">{label}</Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
